/**
 * Sonic Envelope
 * Main Application Logic (Refactored)
 */
import { AudioEngine } from './audio/AudioEngine.js';
import { SessionManager } from './js/SessionManager.js';
import { Visualizer } from './js/Visualizer.js';
import { MidiManager } from './js/MidiManager.js';
import { DemoManager } from './js/DemoManager.js';
import { Sequencer } from './js/Sequencer.js';
import { AudioRecorder } from './js/AudioRecorder.js';
import { VideoRecorder } from './js/VideoRecorder.js';

// --- Versioning ---
try {
    document.getElementById("app-version").textContent = __APP_VERSION__;
    document.getElementById("build-date").textContent = __BUILD_DATE__;
} catch {
    // Fallback for non-Vite environments
    console.warn("Versioning constants not defined.");
    document.getElementById('app-version').textContent = "1.9.2-dev";
    document.getElementById('build-date').textContent = new Date().toLocaleDateString();
}

// --- UI Elements ---
const startBtn = document.getElementById('start-btn');
const overlay = document.getElementById('overlay');
const canvas = document.getElementById('visualizer');
const waveButtons = document.querySelectorAll('.wave-btn');
// Sliders - FX
// Replaced by generic delegation
// System Controls
const muteBtn = document.getElementById('mute-btn');
const iconSound = document.getElementById('icon-sound');
const iconMute = document.getElementById('icon-mute');
const masterVolInput = document.getElementById('master-vol');
const resetBtn = document.getElementById('reset-btn');
const demoBtn = document.getElementById('demo-btn');
const themeBtn = document.getElementById('theme-btn');
const seqBtn = document.getElementById('seq-btn');
const kbBtn = document.getElementById('kb-btn');
const fxBtn = document.getElementById('fx-btn');
const seqUI = document.getElementById('sequencer');
const fxRack = document.querySelector('#fx-rack-container');
const fxResetBtn = document.getElementById('fx-reset-btn');
const fxRandomBtn = document.getElementById('fx-random-btn');
const polyToggle = document.getElementById('poly-toggle');
const backendToggle = document.getElementById('backend-toggle');
const keyboard = document.getElementById('keyboard');
const midiBtn = document.getElementById('midi-btn');

// App Details Overlay
document.getElementById('branding')?.addEventListener('click', () => {
    document.getElementById('about-overlay').show();
});

// --- Config ---
const config = {
    minFreq: 60,   // Hz
    maxFreq: 2000, // Hz
    minFilter: 100,
    maxFilter: 5000,
    baseGain: 0.5
};

// --- State ---
const engine = new AudioEngine();
const session = new SessionManager(engine);
const midiManager = new MidiManager();
const audioRecorder = new AudioRecorder(engine);
const videoRecorder = new VideoRecorder(engine, canvas);
let sequencer = null;
let heldNotes = new Set(); // Track held notes for Arp
let isPlaying = false;
let analyser = null; // Reference to analyser from engine

// Check for MIDI support on splash
if (!navigator.requestMIDIAccess && !window.__TAURI__) {
    const badge = document.getElementById('midi-badge');
    if (badge) badge.style.display = 'none';
}

// --- Visualization ---
const visualizer = new Visualizer(canvas, null);
let demoStateSnapshot = null;

function captureState() {
    const state = {
        waveform: document.querySelector('.wave-btn.active')?.dataset.wave || 'sawtooth',
        lfo: {
            target: document.getElementById('lfo-target-select')?.value || 'none',
            params: {}
        },
        modules: []
    };
    
    // Capture LFO Params
    document.querySelector('.rack-module[data-id="lfo-module"]')?.querySelectorAll('rotary-knob, slide-switch').forEach(knob => {
        state.lfo.params[knob.getAttribute('data-param')] = parseFloat(knob.value);
    });
    
    document.querySelectorAll('.rack-module').forEach(module => {
        if (module.dataset.id === 'lfo-module' || module.dataset.id === 'tape-module') return; 
        
        const modState = {
            id: module.dataset.id,
            enabled: module.querySelector('.bypass-toggle')?.checked || false,
            params: {}
        };
        
        module.querySelectorAll('rotary-knob, slide-switch').forEach(knob => {
            modState.params[knob.getAttribute('data-param')] = parseFloat(knob.value);
        });
        
        state.modules.push(modState);
    });
    return state;
}

function restoreState(state) {
    if (!state) return;
    console.log("Restoring State:", state);

    // 1. Silence Master Temporarily to hide transitions
    const masterVol = parseFloat(masterVolInput.value);
    engine.setMasterVolume(0);

    // 2. Kill FX Tails (Feedback loops) immediately
    // This stops the demo's echoes from continuing into the user's session
    engine.setEffectParam('delay', 'feedback', 0);
    engine.setEffectParam('reverb', 'mix', 0);
    
    // 3. Short delay to let buffers clear/silence
    setTimeout(() => {
        // Restore Waveform
        const waveBtn = document.querySelector(`.wave-btn[data-wave="${state.waveform}"]`);
        if (waveBtn) {
            waveButtons.forEach(b => b.classList.remove('active'));
            waveBtn.classList.add('active');
            engine.setWaveform(state.waveform);
        }

        // Restore LFO
        if (state.lfo) {
            // Restore Engine
            engine.setLFOTarget(state.lfo.target);
            for (const [param, val] of Object.entries(state.lfo.params)) {
                 engine.setLFOParam(param, val);
            }

            // Restore UI
            const lfoTarget = document.getElementById('lfo-target-select');
            if (lfoTarget) lfoTarget.value = state.lfo.target;
            
            const lfoModule = document.querySelector('.rack-module[data-id="lfo-module"]');
            if (lfoModule) {
                 for (const [param, val] of Object.entries(state.lfo.params)) {
                     const knob = lfoModule.querySelector(`[data-param="${param}"]`);
                     if (knob) knob.value = val;
                 }
            }
        }
        
        // Restore Modules
        state.modules.forEach(mod => {
            const module = document.querySelector(`.rack-module[data-id="${mod.id}"]`);
            if (module) {
                // Restore Toggle
                const toggle = module.querySelector('.bypass-toggle');
                if (toggle) {
                    toggle.checked = mod.enabled;
                    if (mod.enabled) module.classList.remove('disabled');
                    else module.classList.add('disabled');
                    engine.toggleEffect(mod.id, mod.enabled);
                }
                
                // Restore Knobs
                for (const [param, val] of Object.entries(mod.params)) {
                    const knob = module.querySelector(`[data-param="${param}"]`);
                    if (knob) {
                        knob.value = val;
                        
                        const fxId = knob.getAttribute('data-fx');
                        if (fxId === 'lfo') {
                             if (param === 'waveform') {
                                 const waves = ['sine', 'square', 'sawtooth', 'triangle'];
                                 engine.setLFOParam('waveform', waves[Math.round(val)] || 'sine');
                             } else {
                                 engine.setLFOParam(param, val);
                             }
                        } else {
                            engine.setEffectParam(fxId, param, val);
                        }
                    }
                }
            }
        });

        // 4. Restore Master Volume
        engine.setMasterVolume(masterVol);
        showToast("State Restored");
        
    }, 100);
}

const demoManager = new DemoManager(engine, config, {
    onTuneStart: (tune) => {
        showToast(`Demo: ${tune.name}`);
        
        // Update Waveform UI
        const waveBtn = document.querySelector(`.wave-btn[data-wave="${tune.waveform}"]`);
        if (waveBtn) {
            waveButtons.forEach(b => b.classList.remove('active'));
            waveBtn.classList.add('active');
        }

        // Update FX UI (Bypass toggles and Knobs)
        tune.fx.forEach(effect => {
            // Find module
            const module = document.querySelector(`.rack-module[data-id="${effect.id}"]`);
            if (module) {
                // Toggle
                const toggle = module.querySelector('.bypass-toggle');
                if (toggle) {
                    toggle.checked = effect.enabled;
                    if (effect.enabled) module.classList.remove('disabled');
                    else module.classList.add('disabled');
                }

                // Knobs
                if (effect.enabled) {
                    for (const [param, value] of Object.entries(effect.params)) {
                        const knob = module.querySelector(`[data-param="${param}"]`);
                        if (knob) {
                            knob.value = value; // Setter updates visual
                        }
                    }
                }
            }
        });

        // Update LFO UI
        if (tune.lfo) {
            let lfo0 = null;
            if (Array.isArray(tune.lfo)) {
                lfo0 = tune.lfo.find(l => l.index === 0);
            } else {
                lfo0 = tune.lfo;
            }

            if (lfo0) {
                // Target Select
                const lfoTarget = document.getElementById('lfo-target-select');
                if (lfoTarget) lfoTarget.value = lfo0.target;

                // LFO Module Params
                const lfoModule = document.querySelector('.rack-module[data-id="lfo-module"]');
                if (lfoModule) {
                     for (const [param, value] of Object.entries(lfo0.params)) {
                         const knob = lfoModule.querySelector(`[data-param="${param}"]`);
                         if (knob) knob.value = value;
                     }
                }
            }
        }
    },
    onStop: () => {
        if (demoStateSnapshot) {
            restoreState(demoStateSnapshot);
        }
    },
    onNote: (x, y) => {
        // Add ghost visual at computed position
        visualizer.addHeatPoint(x, y);
    }
});

// --- Initialization ---
// Theme Config
const themes = {
    neon: {
        id: 'neon',
        trailGradient: ['rgba(0, 255, 204, 0.6)', 'rgba(255, 0, 255, 0.3)', 'rgba(0,0,0,0)'],
        spectrumHueStart: 180,
        spectrumHueEnd: 360,
        scopeColor: '#00ffcc',
        bgFade: 'rgba(17, 17, 17, 0.2)',
        specColors: ['#000', '#003322', '#00ffcc', '#ff00ff', '#fff']
    },
    fire: {
        id: 'fire',
        trailGradient: ['rgba(255, 200, 0, 0.7)', 'rgba(255, 50, 0, 0.4)', 'rgba(0,0,0,0)'],
        spectrumHueStart: 0,
        spectrumHueEnd: 60,
        scopeColor: '#ffcc00',
        bgFade: 'rgba(20, 5, 0, 0.2)',
        specColors: ['#000', '#331100', '#ff5500', '#ffcc00', '#fff']
    },
    ocean: {
        id: 'ocean',
        trailGradient: ['rgba(0, 255, 255, 0.6)', 'rgba(0, 100, 255, 0.4)', 'rgba(0,0,0,0)'],
        spectrumHueStart: 180,
        spectrumHueEnd: 240,
        scopeColor: '#00aaff',
        bgFade: 'rgba(0, 10, 25, 0.2)',
        specColors: ['#000', '#001133', '#00aaff', '#00ffff', '#fff']
    },
    matrix: {
        id: 'matrix',
        trailGradient: ['rgba(0, 255, 0, 0.8)', 'rgba(0, 50, 0, 0.4)', 'rgba(0,0,0,0)'],
        spectrumHueStart: 100,
        spectrumHueEnd: 140,
        scopeColor: '#0f0',
        bgFade: 'rgba(0, 10, 0, 0.2)',
        specColors: ['#000', '#002200', '#00ff00', '#88ff88', '#fff']
    },
    cyberpunk: {
        id: 'cyberpunk',
        trailGradient: ['rgba(255, 0, 153, 0.7)', 'rgba(0, 204, 255, 0.4)', 'rgba(0,0,0,0)'],
        spectrumHueStart: 300,
        spectrumHueEnd: 340,
        scopeColor: '#f09',
        bgFade: 'rgba(15, 0, 20, 0.2)',
        specColors: ['#000', '#20002c', '#bb00ff', '#ff0099', '#fff']
    },
    sunset: {
        id: 'sunset',
        trailGradient: ['rgba(255, 100, 0, 0.7)', 'rgba(255, 0, 100, 0.4)', 'rgba(0,0,0,0)'],
        spectrumHueStart: 10,
        spectrumHueEnd: 50,
        scopeColor: '#ff6600',
        bgFade: 'rgba(30, 10, 10, 0.2)',
        specColors: ['#000', '#300000', '#ff0055', '#ff9900', '#ffffcc']
    },
    forest: {
        id: 'forest',
        trailGradient: ['rgba(100, 255, 0, 0.6)', 'rgba(0, 100, 50, 0.4)', 'rgba(0,0,0,0)'],
        spectrumHueStart: 80,
        spectrumHueEnd: 160,
        scopeColor: '#6f0',
        bgFade: 'rgba(5, 15, 5, 0.2)',
        specColors: ['#000', '#051a05', '#228b22', '#aaff00', '#fff']
    },
    ghost: {
        id: 'ghost',
        trailGradient: ['rgba(200, 200, 255, 0.5)', 'rgba(100, 100, 150, 0.3)', 'rgba(0,0,0,0)'],
        spectrumHueStart: 200,
        spectrumHueEnd: 220,
        scopeColor: '#ccd',
        bgFade: 'rgba(20, 20, 30, 0.2)',
        specColors: ['#000', '#111', '#445', '#99a', '#fff']
    }
};
let currentTheme = themes.neon;
let themeKeys = Object.keys(themes);

// Set initial theme
visualizer.setTheme(currentTheme);

function toggleTheme() {
    // Cycle theme
    const currentIndex = themeKeys.indexOf(currentTheme.id);
    const nextIndex = (currentIndex + 1) % themeKeys.length;
    const nextId = themeKeys[nextIndex];
    
    currentTheme = themes[nextId];
    visualizer.setTheme(currentTheme);
    
    // Update CSS
    document.body.className = `theme-${nextId}`;
    
    // Log
    showToast(`Theme: ${nextId.toUpperCase()}`);
}

async function connectMidi() {
    const midiBtn = document.getElementById('midi-btn');
    // MIDI
    const midiSuccess = await midiManager.init();
    if (midiSuccess) {
        showToast("MIDI System Ready");
        if (midiBtn) {
            midiBtn.style.color = "#44ff44"; // Greenish
            midiBtn.style.borderColor = "#44ff44";
        }
        
        midiManager.onNoteOn = (note, velocity) => {
            if (engine.ctx && engine.ctx.state === 'suspended') engine.ctx.resume();
            
            // Note to Freq: 440 * 2^((n-69)/12)
            const freq = 440 * Math.pow(2, (note - 69) / 12);
            const gain = velocity / 127;

            heldNotes.add(note);
            if (sequencer) sequencer.updateHeldNotes(heldNotes);
            
            // If Arp Active, don't trigger direct sound?
            if (sequencer && sequencer.arpEnabled && sequencer.isPlaying) {
                // Do nothing
            } else {
                engine.triggerAttack(freq, gain, note); // Use Note Number as ID
            }
            
            // Visuals
            const x = Math.log2(freq / config.minFreq) / Math.log2(config.maxFreq / config.minFreq);
            visualizer.addHeatPoint(Math.max(0, Math.min(1, x)), 0.5);
            
            // Record
            if (session.isRecording) {
                session.recordEvent('noteOn', { freq, gain, id: note }); 
            }
        };
        
        midiManager.onNoteOff = (note) => {
            heldNotes.delete(note);
            if (sequencer) sequencer.updateHeldNotes(heldNotes);

            engine.triggerRelease(note);
            if (session.isRecording) session.recordEvent('noteOff', { id: note });
        };
        
        midiManager.onPitchBend = (val) => {
            engine.setPitchBend(val);
        };

        midiManager.onChannelPressure = (pressure) => {
            // Map Pressure to Filter Frequency (opening up)
            // 0-127 -> minFilter - maxFilter
            const norm = pressure / 127;
            const freq = config.minFilter + norm * (config.maxFilter - config.minFilter);
            engine.setEffectParam('filter', 'frequency', freq);
        };

        midiManager.onProgramChange = (program) => {
            // Map Program 0-N to available Waveforms
            const waves = ['sawtooth', 'square', 'triangle', 'sine', 'organ', 'retro', 'bass', 'vocal', 'ethereal', 'crunch', 'metallic', 'dystopia'];
            const selected = waves[program % waves.length];
            engine.setWaveform(selected);
            
            // Update UI if possible (find button and click/activate it)
            const btn = document.querySelector(`.wave-btn[data-wave="${selected}"]`);
            if (btn) {
                waveButtons.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
            }
            showToast(`Waveform: ${selected.toUpperCase()}`);
        };

        // Transport Control
        const triggerPlayback = () => {
            const playBtn = document.getElementById('tape-play');
            if (playBtn) playBtn.click();
        };
        const stopPlayback = () => {
            const stopBtn = document.getElementById('tape-stop');
            if (stopBtn) stopBtn.click();
        };

        midiManager.onStart = () => {
            showToast("MIDI Start");
            if (sequencer) sequencer.start();
            else triggerPlayback();
        };
        midiManager.onContinue = () => {
            showToast("MIDI Continue");
            if (sequencer && !sequencer.isPlaying) sequencer.start();
            else triggerPlayback();
        };
        midiManager.onStop = () => {
            showToast("MIDI Stop");
            if (sequencer) sequencer.stop();
            else stopPlayback();
        };

        midiManager.onControlChange = (cc, value) => {
            // Map Mod Wheel (1) to Filter
            if (cc === 1) {
                const norm = value / 127;
                const freq = config.minFilter + norm * (config.maxFilter - config.minFilter);
                engine.setEffectParam('filter', 'frequency', freq);
            }
            // Map Sustain Pedal (64)
            else if (cc === 64) {
                const isDown = value >= 64;
                engine.setSustain(isDown);
                if (isDown) showToast("Sustain Active");
            }
            // All Notes Off (123)
            else if (cc === 123) {
                engine.allNotesOff();
                showToast("Panic: All Notes Off");
            }
        };
    } else {
        showToast("MIDI Setup Failed");
    }
}

async function initSystem() {
    try {
        analyser = await engine.init();
        visualizer.analyser = analyser;
        
        // Init Sequencer
        sequencer = new Sequencer(engine.ctx, (freq, velocity, duration, time, id) => {
             engine.triggerAttack(freq, velocity, id, time);
             engine.triggerRelease(id, time + duration);
        });
        
        if (seqUI) seqUI.setSequencer(sequencer);
        
        // Sync UI with Engine defaults if needed
        applyCurrentSettings();
        syncEffectState();
        // Start Visualizer Loop
        drawVisualizer();
        console.log("System Initialized");
    } catch (e) {
        console.error("Initialization Failed:", e);
    }
}

function syncEffectState() {
    document.querySelectorAll('.bypass-toggle').forEach(toggle => {
        const module = toggle.closest('.rack-module');
        const id = module.dataset.id;
        const isEnabled = toggle.checked;
        
        engine.toggleEffect(id, isEnabled);
        
        if (isEnabled) {
            module.classList.remove('disabled');
        } else {
            module.classList.add('disabled');
        }
    });
}

function applyCurrentSettings() {
    // Read all knobs and apply
    document.querySelectorAll('rotary-knob, slide-switch').forEach(knob => {
        const fxId = knob.getAttribute('data-fx');
        const param = knob.getAttribute('data-param');
        const val = parseFloat(knob.value);
        
        if (fxId === 'lfo') {
             if (param === 'waveform') {
                 const waves = ['sine', 'square', 'sawtooth', 'triangle'];
                 engine.setLFOParam('waveform', waves[Math.round(val)] || 'sine');
             } else {
                 engine.setLFOParam(param, val);
             }
        } else {
            engine.setEffectParam(fxId, param, val);
        }
    });
    
    // LFO Target
    const lfoTarget = document.getElementById('lfo-target-select');
    if (lfoTarget) engine.setLFOTarget(lfoTarget.value);

    engine.setMasterVolume(parseFloat(masterVolInput.value));
    
    // Set Waveform
    const activeBtn = document.querySelector('.wave-btn.active');
    if (activeBtn) engine.setWaveform(activeBtn.dataset.wave);
}

// --- Session / Tape Logic ---
function setupTapeDeck() {
    const recBtn = document.getElementById('tape-rec');
    const stopBtn = document.getElementById('tape-stop');
    const playBtn = document.getElementById('tape-play');
    const saveBtn = document.getElementById('tape-save');
    const loadBtn = document.getElementById('tape-load');
    const importBtn = document.getElementById('tape-import');
    const exportBtn = document.getElementById('tape-export');

    if(!recBtn) return; // UI not ready yet

    recBtn.addEventListener('click', () => {
        session.startRecording();
        recBtn.classList.add('active');
        playBtn.classList.remove('active');
        showToast("Recording Started");
    });

    stopBtn.addEventListener('click', () => {
        const wasRecording = session.isRecording;
        const wasPlaying = session.isPlaying;
        session.stopRecording();
        session.stopPlayback();
        recBtn.classList.remove('active');
        playBtn.classList.remove('active');
        
        if (wasRecording) showToast("Recording Stopped");
        else if (wasPlaying) showToast("Playback Stopped");
        else showToast("Stopped");
    });

    playBtn.addEventListener('click', () => {
        if (session.events.length === 0) {
            showToast("No recording to play");
            return;
        }
        
        playBtn.classList.add('active');
        showToast("Playback Started");
        
        session.play({
            onStart: (x, y, id = 'mouse') => {
                // Simulate params
                processInput(x * window.innerWidth, y * window.innerHeight, id, 'start');
            },
            onMove: (x, y, id = 'mouse') => {
                processInput(x * window.innerWidth, y * window.innerHeight, id, 'move');
            },
            onEnd: (id = 'mouse') => {
                processInput(null, null, id, 'end');
            },
            onNoteOn: (freq, gain, id) => {
                engine.triggerAttack(freq, gain, id);
                // Visuals
                const x = Math.log2(freq / config.minFreq) / Math.log2(config.maxFreq / config.minFreq);
                visualizer.addHeatPoint(Math.max(0, Math.min(1, x)), 0.5);
            },
            onNoteOff: (id) => {
                engine.triggerRelease(id);
            }
        });
        
        session.onPlaybackEnd = () => {
            playBtn.classList.remove('active');
            showToast("Playback Finished");
        };
    });

    saveBtn.addEventListener('click', () => {
        const data = session.serialize();
        if (data) {
            localStorage.setItem('sonic-envelope-session', data);
            showToast('Session saved to LocalStorage!');
        }
    });

    loadBtn.addEventListener('click', () => {
        const data = localStorage.getItem('sonic-envelope-session');
        if (data) {
            if (session.deserialize(data)) {
                showToast('Session loaded!');
            }
        } else {
            showToast('No saved session found.');
        }
    });
    
    importBtn.addEventListener('click', () => {
        const data = prompt("Paste session string (Base64):");
        if (data) {
            if (session.deserialize(data)) {
                showToast('Session imported!');
            } else {
                showToast('Invalid session data.');
            }
        }
    });

    exportBtn.addEventListener('click', () => {
        const success = session.exportToFile(`sonic-session-${new Date().toISOString().slice(0,10)}.txt`);
        if (success) showToast("Exported to File");
        else showToast("Nothing to export");
    });
    
    // Export (Double click save?) or a dedicated button
    // Let's make Save also copy to clipboard? Or add export.
    saveBtn.addEventListener('dblclick', () => {
        const data = session.serialize();
        if(data) {
            navigator.clipboard.writeText(data).then(() => showToast("Session copied to clipboard!"));
        }
    });
}

// Call setup after init
setTimeout(setupTapeDeck, 100);
setTimeout(setupAudioRecorder, 100);
setTimeout(setupVideoRecorder, 100);

// --- Audio Recorder Logic ---
function setupAudioRecorder() {
    const recStartBtn = document.getElementById('audio-rec-start');
    const recStopBtn = document.getElementById('audio-rec-stop');
    const controls = document.getElementById('audio-rec-controls');
    const status = document.getElementById('rec-status');
    const videoBtn = document.getElementById('video-rec-start'); // To hide it

    if (!recStartBtn) return;

    recStartBtn.addEventListener('click', () => {
        // Ensure Audio Context is ready
        if (engine.ctx && engine.ctx.state === 'suspended') engine.ctx.resume();
        else if (!engine.ctx) handleStart();

        if (audioRecorder.start()) {
            recStartBtn.style.display = 'none';
            videoBtn.style.display = 'none';
            controls.style.display = 'flex';
            status.textContent = "RECORDING AUDIO...";
            status.style.color = "#ff4444";
            status.classList.add('blink'); 
            showToast("Audio Recording Started");
        } else {
            showToast("Failed to start recording");
        }
    });

    recStopBtn.addEventListener('click', () => {
        // Check which one is recording
        if (audioRecorder.isRecording) {
            if (audioRecorder.stop()) {
                recStartBtn.style.display = 'flex'; // This assumes audio was started. Logic needs to be generic or separate.
                // Reset UI
                resetRecUI();
                showToast("Audio Saved!");
            }
        } else if (videoRecorder.isRecording) {
            if (videoRecorder.stop()) {
                resetRecUI();
                showToast("Video Saved!");
            }
        }
    });
}

function setupVideoRecorder() {
    const videoStartBtn = document.getElementById('video-rec-start');
    const audioStartBtn = document.getElementById('audio-rec-start');
    const recStopBtn = document.getElementById('audio-rec-stop'); // Reuse stop button
    const controls = document.getElementById('audio-rec-controls');
    const status = document.getElementById('rec-status');

    if (!videoStartBtn) return;

    videoStartBtn.addEventListener('click', () => {
        if (engine.ctx && engine.ctx.state === 'suspended') engine.ctx.resume();
        else if (!engine.ctx) handleStart();

        if (videoRecorder.start()) {
            videoStartBtn.style.display = 'none';
            audioStartBtn.style.display = 'none';
            controls.style.display = 'flex';
            status.textContent = "RECORDING VIDEO...";
            status.style.color = "#44aaff";
            status.classList.add('blink');
            showToast("Video Recording Started (UI Hidden)");
        } else {
            showToast("Failed to start video recording");
        }
    });
}

function resetRecUI() {
    const audioStartBtn = document.getElementById('audio-rec-start');
    const videoStartBtn = document.getElementById('video-rec-start');
    const controls = document.getElementById('audio-rec-controls');
    const status = document.getElementById('rec-status');
    
    audioStartBtn.style.display = 'flex'; // Or inline-flex? Check CSS.
    videoStartBtn.style.display = 'flex';
    controls.style.display = 'none';
    status.textContent = "READY";
    status.style.color = "#666";
    status.classList.remove('blink');
}

// --- Helpers ---
function showToast(msg) {
    const toast = document.getElementById('toast');
    if (toast) toast.show(msg);
    console.log(msg);
}

// --- Interaction Handlers ---
async function handleStart() {
    await initSystem();
    if (engine.ctx) {
        console.log(`[Audio] Context State before resume: ${engine.ctx.state}`);
        engine.ctx.resume().then(() => {
            console.log(`[Audio] Context Resumed successfully. State: ${engine.ctx.state}`);
            // Init LFO UI state
            updateLFOUI();
        }).catch(err => {
            console.error("[Audio] Failed to resume context:", err);
            showToast("Error: Audio Device Failed");
        });
    }
    overlay.classList.add('hidden');
}

function handleInputStart(e) {
    if (e.target.closest('.interactive-ui')) return;
    if (e.target.closest('.dropdown-menu')) return; // Ignore clicks on dropdown
    if (document.querySelector('.dropdown-menu')) return; // Ignore clicks elsewhere if dropdown is open (it will close it)
    
    if (!engine.ctx) return;
    
    // Stop Demo on user interaction
    if (demoManager.isPlaying) {
        demoManager.stop();
        demoBtn.classList.remove('active');
        demoBtn.textContent = "DEMO";
        showToast("Demo Interrupted");
    }
    
    if (session.isPlaying) return; 
    
    isPlaying = true; // Global play state for visualizer loop if needed, but voices track themselves
    
    if (e.type === 'mousedown') {
        processInput(e.clientX, e.clientY, 'mouse', 'start');
    } else if (e.changedTouches) {
        for (let i = 0; i < e.changedTouches.length; i++) {
            const t = e.changedTouches[i];
            processInput(t.clientX, t.clientY, t.identifier, 'start');
        }
    }
}

function handleInputMove(e) {
    if (session.isPlaying) return;
    if (!engine.ctx) return;
    
    // For mouse, we only track if isPlaying (button down)
    if (e.type === 'mousemove' && !isPlaying) return;

    if (e.type === 'mousemove') {
        processInput(e.clientX, e.clientY, 'mouse', 'move');
    } else if (e.changedTouches) {
        for (let i = 0; i < e.changedTouches.length; i++) {
            const t = e.changedTouches[i];
            processInput(t.clientX, t.clientY, t.identifier, 'move');
        }
    }
}

function handleInputEnd(e) {
    if (!engine.ctx) return;
    if (session.isPlaying) return;

    if (e.type === 'mouseup') {
        isPlaying = false;
        processInput(null, null, 'mouse', 'end');
    } else if (e.changedTouches) {
        for (let i = 0; i < e.changedTouches.length; i++) {
            const t = e.changedTouches[i];
            processInput(null, null, t.identifier, 'end');
        }
    }
    
    // If no touches left, isPlaying = false? 
    // Simplified: isPlaying tracks if ANY input is active usually, but for polyphony we care about voices.
}

function processInput(clientX, clientY, id, type) {
    if (type === 'end') {
        engine.triggerRelease(id);
        if (session.isRecording) session.recordEvent('end', { id });
        return;
    }

    // Normalize
    const x = clientX / window.innerWidth;
    const y = clientY / window.innerHeight;
    
    // Visuals
    visualizer.addHeatPoint(x, y);

    // Audio
    const freq = config.minFreq * Math.pow(config.maxFreq / config.minFreq, x);
    const filterFreq = config.minFilter * Math.pow(config.maxFilter / config.minFilter, 1 - y);
    
    // We only trigger attack on 'start'. On 'move' we slide pitch.
    if (type === 'start') {
        engine.triggerAttack(freq, config.baseGain, id);
    } else {
        engine.setPitch(freq, id);
    }
    
    // Global parameters (Y-axis filter) - still global for now?
    // In Paraphony, Filter is global. So last touch wins? Or average?
    // Let's let every touch update the global filter for chaotic fun.
    engine.setEffectParam('filter', 'frequency', filterFreq);

    // Record
    if (session.isRecording) {
        session.recordEvent(type, { x, y, id });
    }
}

function updateParams(e) {
    // Deprecated by processInput refactor
}

// --- Visualizer Loop ---
function drawVisualizer() {
    requestAnimationFrame(drawVisualizer);
    visualizer.draw();
}

// Event Listeners
startBtn.addEventListener('click', handleStart);
themeBtn.addEventListener('click', toggleTheme);

// Toggle Keyboard
if (kbBtn && keyboard) {
    kbBtn.addEventListener('click', () => {
        if (keyboard.style.display === 'none') {
            keyboard.style.display = 'block';
            kbBtn.style.color = '#fff';
            kbBtn.style.borderColor = '#fff';
            
            // Reset Position to bottom center
            keyboard.style.top = 'auto';
            keyboard.style.bottom = '100px';
            keyboard.style.left = '50%';
            keyboard.style.transform = 'translateX(-50%)';
        } else {
            keyboard.style.display = 'none';
            kbBtn.style.color = '#888';
            kbBtn.style.borderColor = 'rgba(255,255,255,0.2)';
        }
    });
}

// Toggle Sequencer
if (seqBtn) {
    seqBtn.addEventListener('click', () => {
        if (!seqUI) return;
        if (seqUI.style.display === 'none') {
            seqUI.style.display = 'block';
            seqBtn.style.color = '#fff';
            seqBtn.style.borderColor = '#fff';
        } else {
            seqUI.style.display = 'none';
            seqBtn.style.color = '#888';
            seqBtn.style.borderColor = 'rgba(255,255,255,0.2)';
        }
    });
}

// Toggle FX Rack
if (fxBtn && fxRack) {
    // Initial state check - on mobile it might be hidden by CSS
    // We can check computed style
    fxBtn.addEventListener('click', () => {
        const currentDisplay = window.getComputedStyle(fxRack).display;
        
        if (currentDisplay === 'none') {
            fxRack.style.display = 'flex';
            fxBtn.style.color = '#fff';
            fxBtn.style.borderColor = '#fff';
        } else {
            fxRack.style.display = 'none';
            fxBtn.style.color = '#888';
            fxBtn.style.borderColor = 'rgba(255,255,255,0.2)';
        }
    });
}
// Mouse
window.addEventListener('mousedown', handleInputStart);
window.addEventListener('mousemove', handleInputMove);
window.addEventListener('mouseup', handleInputEnd);
// Touch
window.addEventListener('touchstart', (e) => { 
    if (!e.target.closest('.interactive-ui') && !e.target.closest('#overlay') && !e.target.closest('app-overlay')) {
        e.preventDefault(); 
        handleInputStart(e);
    }
}, { passive: false });
window.addEventListener('touchmove', (e) => { 
    if (!e.target.closest('.interactive-ui') && !e.target.closest('#overlay') && !e.target.closest('app-overlay')) {
        e.preventDefault(); 
        handleInputMove(e);
    }
}, { passive: false });
window.addEventListener('touchend', (e) => { 
    if (!e.target.closest('.interactive-ui') && !e.target.closest('#overlay') && !e.target.closest('app-overlay')) {
        e.preventDefault(); 
        handleInputEnd(e); 
    }
});
window.addEventListener('touchcancel', (e) => { 
    if (!e.target.closest('.interactive-ui') && !e.target.closest('#overlay') && !e.target.closest('app-overlay')) {
        e.preventDefault(); 
        handleInputEnd(e); 
    }
});
// UI Controls
waveButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
        waveButtons.forEach(b => b.classList.remove('active'));
        e.currentTarget.classList.add('active');
        engine.setWaveform(e.currentTarget.dataset.wave);
    });
});
// Generic FX Parameter Logic (Knobs & Sliders)
let currentLFOIndex = 0;
const lfoSelect = document.getElementById('lfo-select');
const lfoTargetSelect = document.getElementById('lfo-target-select');

// LFO Selector Logic
if (lfoSelect) {
    lfoSelect.addEventListener('change', (e) => {
        currentLFOIndex = parseInt(e.target.value);
        updateLFOUI();
    });
}

function updateLFOUI() {
    const state = engine.getLFOState(currentLFOIndex);
    if (!state) return;

    // Update Knobs
    const lfoModule = document.querySelector('.rack-module[data-id="lfo-module"]');
    if (!lfoModule) return;
    
    const rateKnob = lfoModule.querySelector('rotary-knob[data-param="rate"]');
    if (rateKnob) rateKnob.value = state.rate;

    const depthKnob = lfoModule.querySelector('rotary-knob[data-param="depth"]');
    if (depthKnob) depthKnob.value = state.depth;

    // Update Waveform
    const waveSwitch = lfoModule.querySelector('slide-switch[data-param="waveform"]');
    if (waveSwitch) {
        const waves = ['sine', 'square', 'sawtooth', 'triangle'];
        waveSwitch.value = waves.indexOf(state.waveform);
    }

    // Update Target
    if (lfoTargetSelect) {
        lfoTargetSelect.value = state.target;
    }
}

document.querySelector('#fx-rack-container').addEventListener('input', (e) => {
    const target = e.target;
    // Check if it's our custom element (tagName usually UPPERCASE)
    if (target.tagName === 'ROTARY-KNOB' || target.tagName === 'SLIDE-SWITCH') {
        const fxId = target.getAttribute('data-fx');
        const param = target.getAttribute('data-param');
        const val = parseFloat(target.value);
        
        if (fxId === 'lfo') {
             // Handle LFO specific params
             if (param === 'waveform') {
                 const waves = ['sine', 'square', 'sawtooth', 'triangle'];
                 engine.setLFOParam(currentLFOIndex, 'waveform', waves[Math.round(val)] || 'sine');
             } else {
                 engine.setLFOParam(currentLFOIndex, param, val);
             }
        } else {
            engine.setEffectParam(fxId, param, val);
        }
    }
});
// LFO Target Select
if (lfoTargetSelect) {
    lfoTargetSelect.addEventListener('change', (e) => {
        engine.setLFOTarget(currentLFOIndex, e.target.value);
        showToast(`LFO ${currentLFOIndex + 1} Target: ${e.target.options[e.target.selectedIndex].text}`);
    });
}
// Bypass Toggles & Fold Logic
// const fxRack declaration moved to top

// Prevent drag start on interactive elements
fxRack.addEventListener('mousedown', (e) => {
    if (e.target.closest('.fold-btn') || e.target.closest('.bypass-toggle') || e.target.closest('rotary-knob') || e.target.closest('button') || e.target.tagName === 'SELECT') {
        e.stopPropagation(); // Don't trigger drag on module
    }
});

fxRack.addEventListener('click', (e) => {
    // Fold Button
    if (e.target.closest('.fold-btn')) {
        const module = e.target.closest('.rack-module');
        module.classList.toggle('folded');
        return;
    }
});

fxRack.addEventListener('change', (e) => {
    if (e.target.classList.contains('bypass-toggle')) {
        const module = e.target.closest('.rack-module');
        const id = module.dataset.id;
        const isEnabled = e.target.checked;
        
        engine.toggleEffect(id, isEnabled);
        showToast(`${id.toUpperCase()} ${isEnabled ? 'Enabled' : 'Bypassed'}`);
        
        if (isEnabled) {
            module.classList.remove('disabled');
        } else {
            module.classList.add('disabled');
        }
    }
});
// --- Drag & Drop Rack Logic ---
const rackContainer = document.getElementById('fx-rack-container');
let draggedModule = null;
rackContainer.addEventListener('dragstart', (e) => {
    draggedModule = e.target.closest('.rack-module');
    e.dataTransfer.effectAllowed = 'move';
    // Make transparent
    setTimeout(() => draggedModule.classList.add('dragging'), 0);
});
rackContainer.addEventListener('dragend', () => {
    draggedModule.classList.remove('dragging');
    draggedModule = null;
    
    // Update Engine Routing
    const newOrder = Array.from(rackContainer.querySelectorAll('.rack-module'))
        .map(el => el.dataset.id);
    console.log("New FX Order:", newOrder);
    engine.updateRouting(newOrder);
});
rackContainer.addEventListener('dragover', (e) => {
    e.preventDefault();
    const afterElement = getDragAfterElement(rackContainer, e.clientY);
    if (afterElement == null) {
        rackContainer.appendChild(draggedModule);
    } else {
        rackContainer.insertBefore(draggedModule, afterElement);
    }
});
function getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll('.rack-module:not(.dragging)')];
    return draggableElements.reduce((closest, child) => {
        const box = child.getBoundingClientRect();
        const offset = y - box.top - box.height / 2;
        if (offset < 0 && offset > closest.offset) {
            return { offset: offset, element: child };
        } else {
            return closest;
        }
    }, { offset: Number.NEGATIVE_INFINITY }).element;
}
// Enable draggable on modules
document.querySelectorAll('.rack-module').forEach(el => {
    el.setAttribute('draggable', true);
});
// System
masterVolInput.addEventListener('input', (e) => engine.setMasterVolume(parseFloat(e.target.value)));
resetBtn.addEventListener('click', () => {
    console.log("Resetting...");
    initSystem();
});
demoBtn.addEventListener('click', () => {
    if (demoManager.isPlaying) {
        demoManager.stop();
        demoBtn.classList.remove('active');
        demoBtn.textContent = "DEMO";
        showToast("Demo Stopped");
    } else {
        // Ensure audio context is running
        if (engine.ctx && engine.ctx.state === 'suspended') engine.ctx.resume();
        else if (!engine.ctx) handleStart(); // Init if not ready

        demoStateSnapshot = captureState(); // Capture current state
        demoManager.start();
        demoBtn.classList.add('active');
        demoBtn.textContent = "STOP DEMO";
    }
});
muteBtn.addEventListener('click', () => {
    const isMuted = engine.toggleMute();
    if (isMuted) {
        iconSound.style.display = 'none';
        iconMute.style.display = 'block';
    } else {
        iconSound.style.display = 'block';
        iconMute.style.display = 'none';
    }
});
// FX Toolbar Controls
// (Declarations moved to top)

// Backend Toggle (Tauri Only)
if (window.__TAURI__) {
    if (backendToggle) {
        backendToggle.style.display = 'inline-block';
        
        backendToggle.addEventListener('change', async (e) => {
            const isRust = e.target.active;
            const success = await engine.setBackend(isRust ? 'rust' : 'web');
            
            if (success) {
                showToast(`Audio Backend: ${isRust ? 'RUST' : 'WEB AUDIO'}`);
            } else {
                // Revert toggle if failed
                e.target.active = !isRust; 
                showToast("Backend Switch Failed");
            }
        });
    }
}

if (midiBtn) {
    midiBtn.addEventListener('click', connectMidi);
}

// Keyboard Integration
if (keyboard) {
    keyboard.addEventListener('noteon', (e) => {
        // Stop Demo on keyboard interaction
        if (demoManager.isPlaying) {
            demoManager.stop();
            demoBtn.classList.remove('active');
            demoBtn.textContent = "DEMO";
            showToast("Demo Interrupted");
        }

        const { freq, id, velocity } = e.detail;
        const gain = velocity !== undefined ? velocity : 0.8;

        if (engine.ctx) {
            if (engine.ctx.state === 'suspended') engine.ctx.resume();
            
            const note = Math.round(12 * Math.log2(freq / 440) + 69);
            heldNotes.add(note);
            if (sequencer) sequencer.updateHeldNotes(heldNotes);

            // If Arp Active, don't trigger direct sound?
            if (sequencer && sequencer.arpEnabled && sequencer.isPlaying) {
                // Do nothing, arp picks it up
            } else {
                 engine.triggerAttack(freq, gain, id);
            }
            
            // Visual feedback on main canvas?
            // Maybe add a heat point at random X/Y or mapped to pitch?
            // Map freq to X
            const x = Math.log2(freq / config.minFreq) / Math.log2(config.maxFreq / config.minFreq);
            visualizer.addHeatPoint(Math.max(0, Math.min(1, x)), 0.5);

            if (session.isRecording) {
                session.recordEvent('noteOn', { freq, gain, id });
            }
        }
    });

    keyboard.addEventListener('noteoff', (e) => {
        const { id, freq } = e.detail;
        
        if (freq) {
             const note = Math.round(12 * Math.log2(freq / 440) + 69);
             heldNotes.delete(note);
             if (sequencer) sequencer.updateHeldNotes(heldNotes);
        }
        
        if (engine.ctx) {
            engine.triggerRelease(id);
            
            if (session.isRecording) {
                session.recordEvent('noteOff', { id });
            }
        }
    });
}

polyToggle.addEventListener('change', (e) => {
    const isPoly = e.target.active;
    engine.setPolyphonyMode(isPoly ? 'poly' : 'mono');
    showToast(`Mode: ${isPoly ? 'POLYPHONIC' : 'MONOPHONIC'}`);
});

fxResetBtn.addEventListener('click', () => {
    // Reset Knobs and Switches
    document.querySelectorAll('rotary-knob, slide-switch').forEach(knob => {
        if (knob.hasAttribute('data-default')) {
            const def = parseFloat(knob.getAttribute('data-default'));
            knob.value = def; // Triggers 'input' event via setter
        }
    });

    // Reset LFO Targets (Both instances)
    if (lfoTargetSelect) {
        // Reset in Engine
        engine.setLFOTarget(0, 'none');
        engine.setLFOTarget(1, 'none');
        
        // Reset UI for current selection
        lfoTargetSelect.value = 'none';
        showToast("FX & LFO Reset");
    }
});
fxRandomBtn.addEventListener('click', () => {
    document.querySelectorAll('rotary-knob, slide-switch').forEach(knob => {
        const min = knob.min;
        const max = knob.max;
        const step = knob.step;
        
        if (knob.tagName === 'SLIDE-SWITCH') {
            // For switch, max is implicit from labels length usually, but we haven't set min/max attributes on it explicitly in the JS class...
            // Wait, SlideSwitch JS class doesn't have min/max getters!
            // We should rely on 'options.length'
            // For now, let's assume if it has data-labels it might work or we skip it if properties missing.
            // Actually, my SlideSwitch implementation doesn't support 'min' 'max' properties yet.
            // I should add them or handle it differently.
            
            // Let's just randomize based on labels count if available
            // Accessing internal _options is bad practice.
            // Let's skip randomizing switches for now OR update SlideSwitch to support it.
            // The user wanted a slide switch for the rack.
            
            // Let's try to read labels attribute
            const labels = knob.getAttribute('labels');
            if (labels) {
                const count = labels.split(',').length;
                const rand = Math.floor(Math.random() * count);
                knob.value = rand;
            }
            return;
        }

        // Random value
        let rand = Math.random() * (max - min) + min;
        
        // Snap to step
        rand = Math.round(rand / step) * step;
        
        knob.value = rand; // Triggers 'input' event via setter
    });
    showToast("FX Randomized");
});


// --- Dropdown Helper ---
function setupLongPressDropdown(element, itemsGetter, onSelect) {
    let pressTimer;
    let isLongPress = false;

    const showMenu = (e) => {
        // Remove existing
        const existing = document.querySelector('.dropdown-menu');
        if (existing) existing.remove();

        const items = itemsGetter();
        if (!items || items.length === 0) return;

        const menu = document.createElement('div');
        menu.className = 'dropdown-menu';
        
        items.forEach((item, index) => {
            const div = document.createElement('div');
            div.className = 'dropdown-item';
            div.textContent = item.label;
            if (item.active) div.classList.add('active');
            
            div.addEventListener('click', (ev) => {
                ev.preventDefault();
                ev.stopImmediatePropagation();
                ev.stopPropagation();
                onSelect(item.value, index);
                menu.remove();
            });
            menu.appendChild(div);
        });

        document.body.appendChild(menu);

        // Position
        const rect = element.getBoundingClientRect();
        menu.style.top = `${rect.bottom + 5}px`;
        // Align right edge if close to screen edge, otherwise left
        if (rect.left + 150 > window.innerWidth) {
            menu.style.right = `${window.innerWidth - rect.right}px`;
        } else {
            menu.style.left = `${rect.left}px`;
        }

        // Close on outside click
        const closeHandler = (ev) => {
            if (!menu.contains(ev.target)) {
                menu.remove();
                document.removeEventListener('click', closeHandler);
            }
        };
        // Delay adding listener to avoid immediate trigger
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    };

    const start = (e) => {
        isLongPress = false;
        pressTimer = setTimeout(() => {
            isLongPress = true;
            showMenu(e);
            // Vibrate if mobile
            if (navigator.vibrate) navigator.vibrate(50);
        }, 500);
    };

    const cancel = () => {
        clearTimeout(pressTimer);
    };

    element.addEventListener('mousedown', start);
    element.addEventListener('touchstart', start, { passive: true });
    
    element.addEventListener('mouseup', cancel);
    element.addEventListener('mouseleave', cancel);
    element.addEventListener('touchend', cancel, { passive: true });
    element.addEventListener('touchmove', cancel, { passive: true });
    
    // Intercept click if long press occurred
    element.addEventListener('click', (e) => {
        if (isLongPress) {
            e.stopImmediatePropagation();
            e.preventDefault();
        }
    }, true); 
}

// Setup Dropdowns
setupLongPressDropdown(
    themeBtn, 
    () => themeKeys.map(key => ({ 
        label: key, 
        value: key, 
        active: currentTheme.id === key 
    })), 
    (key) => {
        currentTheme = themes[key];
        visualizer.setTheme(currentTheme);
        document.body.className = `theme-${key}`;
        showToast(`Theme: ${key.toUpperCase()}`);
    }
);

setupLongPressDropdown(
    demoBtn,
    () => demoManager.tunes.map((t, i) => ({
        label: t.name,
        value: i,
        active: demoManager.isPlaying && demoManager.currentTuneIdx === i
    })),
    (index) => {
        // Ensure audio context
        if (engine.ctx && engine.ctx.state === 'suspended') engine.ctx.resume();
        else if (!engine.ctx) handleStart();

        // Capture state if starting new
        if (!demoManager.isPlaying) {
            demoStateSnapshot = captureState();
        }
        
        demoManager.isPlaying = true; // Force true to allow playTune
        demoManager.playTune(index);
        
        demoBtn.classList.add('active');
        demoBtn.textContent = "STOP DEMO";
        showToast(`Playing: ${demoManager.tunes[index].name}`);
    }
);